import { Card, Skeleton, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import FilterDonut from "./FilterDonut";
import ReloadButton from "../../../common/reload-button/ReloadButton";
import DonutChart, { IPieData } from "../../../common/PieChart";
import { useNotificationContext } from "../../../../context/notificationContext";
import { useProtectedAxiosInstance } from "../../../../hooks/axiosInstance";
import errorResolver from "../../../../utils/error-resolver";
import { format } from "date-fns";
import MuiLinearProgress from "../../common/MuiLinearProgress";
import {
  BroadcastingDetailsRequest,
  BroadcastingReportListColumnFilterable,
  VoiceBroadCastReportSchema,
} from "../../../../services/Endpoints/broadcast";
import { filterOptions } from "./filterOptions";
import { FilterOperatorEnum } from "../../../../services/Schemas/ListQueryString";

interface IBroadCastingMedSecProps {
  type?: string;
  id?: number | string;
  loading?: boolean;
  initialReports?: VoiceBroadCastReportSchema[] | null;
}

const COLORS = {
  failed: "#E74C3C", // Vibrant Red
  blocked: "#2980B9", // Bright Blue
  no_answer: "#27AE60", // Emerald Green
  busy: "#8E44AD", // Purple
  rejected: "#F39C12", // Orange
  unreachable: "#16A085", // Turquoise
  device_off: "#D35400", // Pumpkin
  invalid_number: "#2C3E50", // Dark Slate Blue
  canceled: "#C0392B", // Dark Red
  retry: "#7F8C8D", // Gray
};

export const statusesOverall = {
  waiting_for_send: {
    value: ["created", "waiting_for_send", "queued"],
    key: "در صف ارسال",
  },
  sending: { value: ["sending"], key: "در حال ارسال" },
  success: { value: ["answered"], key: "پاسخ داده شده" },
  failed: {
    value: [
      "failed",
      "canceled",
      "invalid_number",
      "device_off",
      "unreachable",
      "rejected",
      "busy",
      "no_answer",
      "blocked",
      "retry",
    ],
    key: "ناموفق",
  },
};

const failedStatus = {
  failed: "ناموفق",
  blocked: "مسدود شده",
  no_answer: "بی پاسخ",
  busy: "مشغول",
  rejected: "رد داده شده",
  unreachable: "خارج از دسترس",
  device_off: "خاموش",
  invalid_number: "شماره نامعتبر",
  canceled: "کنسل شده",
  retry: "تلاش دوباره",
};

const handleStatus = (reports: VoiceBroadCastReportSchema[]) => {
  const result = {};

  reports.forEach((report) => {
    if (
      !report.status ||
      ["created", "waiting_for_send", "answered", "sending", "queued"].includes(
        report.status,
      ) ||
      report.total <= 0
    ) {
      return;
    }

    // @ts-ignore
    if (!result[report.status]) {
      // @ts-ignore
      result[report.status as unknown] = {
        value: 0,
        name: failedStatus[report.status as "failed"],
        color: COLORS[report.status as "failed"] ?? "#ccc",
        valueDisplay: ` تماس`,
      };
    }
    // @ts-ignore
    result[report.status as unknown].value += report.total;
  });

  return Object.values(result);
};

export default function BroadcastFailStatusReport({
  id,
  type,
  loading,
  initialReports = null,
}: Readonly<IBroadCastingMedSecProps>) {
  const [filter1, setFilter1] = useState(filterOptions[0].value.start);

  const [reports, setReports] = useState<VoiceBroadCastReportSchema[]>([]);
  const [timeFilter, setTimeFilter] = useState<string[] | null>(null);
  const [waiting, setWaiting] = useState(false);

  const { setNotification } = useNotificationContext();
  const protectedInstance = useProtectedAxiosInstance();
  const [reload, setReload] = useState(false);

  const handleReload = () => {
    setReload(!reload);
  };

  const getData = async () => {
    if (id) {
      try {
        setWaiting(true);
        const broadcastingDetailsRequest = new BroadcastingDetailsRequest(
          protectedInstance,
        );
        const { data: _data } = await broadcastingDetailsRequest.report(
          {
            broadcastingNumber: id.toString(),
          },
          {
            filters: timeFilter
              ? [
                  {
                    column: BroadcastingReportListColumnFilterable.UPDATED_AT,
                    operator: FilterOperatorEnum.BETWEEN,
                    value: timeFilter,
                  },
                ]
              : [],
          },
        );
        setReports(_data.number_reports);
        setWaiting(false);
      } catch (error: any) {
        errorResolver({ error, setNotification });
        setWaiting(false);
        // navigate("/404");
        console.error(error.message);
      }
    }
  };

  useEffect(() => {
    initialReports && getData();
  }, [timeFilter, reload]);

  useEffect(() => {
    initialReports && setReports(initialReports);
  }, [initialReports]);

  const handleTimeFilter = (filter1: { start: string; end: string }) => {
    if (!waiting) {
      setFilter1(filter1.start);
      setTimeFilter([
        format(filter1.start, "yyyy-MM-dd HH:mm:ss"),
        format(filter1.end, "yyyy-MM-dd HH:mm:ss"),
      ]);
    }
  };

  return (
    <Stack position="relative">
      {waiting && <MuiLinearProgress />}
      <Card
        height="100%"
        bgcolor="#fff"
        component={Stack}
        // direction="row"
        // alignItems="center"
        gap="10px"
        sx={{
          borderRadius: "8px",
          py: "14px",
          px: "16px",
          borderColor: (theme) => theme.palette.others.borderColor,
          borderWidth: "1px",
          borderStyle: "solid",
          boxShadow: "none",
          flex: "1",
        }}
      >
        <Stack direction="row" justifyContent="space-between">
          <Stack direction="row" gap="10px" justifyContent="start">
            {!loading ? (
              <FilterDonut
                setOption={handleTimeFilter}
                optionsList={filterOptions}
                active={
                  filterOptions.filter((itm) => itm.value.start == filter1)[0]
                    ?.key
                }
              />
            ) : (
              Array(5)
                .fill("")
                .map((_, i) => (
                  <Skeleton variant="rectangular" width="80px" height="20px" />
                ))
            )}
          </Stack>
          {!loading ? (
            <ReloadButton reloadAction={handleReload} />
          ) : (
            <Skeleton variant="rectangular" width="40px" height="40px" />
          )}
        </Stack>
        {!loading ? (
          <>
            <Typography
              variant="h6MediumBold"
              color={(thm) => thm.palette.accent.main}
            >
              گزارش تماس‌های ناموفق (
              {reports
                .filter((itm) =>
                  statusesOverall.failed.value.includes(itm.status),
                )
                .reduce((prev, cur) => prev + cur.total, 0)}
              )
            </Typography>
          </>
        ) : (
          <>
            <Skeleton variant="rectangular" width="200px" height="20px" />
            <Skeleton variant="rectangular" width="500px" height="20px" />
          </>
        )}
        <Stack justifyContent="start" alignItems="center" width="100%">
          {reports ? (
            <DonutChart
              data={handleStatus(reports) as IPieData[]}
              pieWrapSx={{
                flexDirection: "row-reverse",
                alignItems: "center",
                width: "100%",
                justifyContent: { xs: "start", md: "space-between" },
              }}
              legendSx={{
                flexDirection: "column",
              }}
              loading={(waiting && reports.length == 0) || loading}
            />
          ) : (
            <Typography
              sx={{
                textAlign: "center",
                width: "100%",
                py: "30px",
              }}
            >
              {!waiting && <>دیتایی جهت نمایش وجود ندارد.</>}
            </Typography>
          )}
        </Stack>
      </Card>
    </Stack>
  );
}
